import { Button, Card, Input, Popconfirm, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type AchievementRow = {
  id: string;
  title: string;
  achieved_on: string;
  level: string;
  activity_code?: string;
  activity_name?: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  evidence_url?: string | null;
};

const levels = ['school', 'district', 'state', 'national', 'other'];

export default function ActivityAchievementsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const level = params.get('level') || undefined;
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.activity.manage') ||
      user?.permissions?.includes('education.admin')
  );

  const achievements = useQuery({
    queryKey: ['education-activity-achievements', q, level],
    queryFn: async () => {
      const res = await api.get<AchievementRow[]>('/education/activity-achievements', {
        params: { q: q || undefined, level },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/education/activity-achievements/${id}`),
    onSuccess: () => {
      message.success(t('activityAchievementDeleted'));
      queryClient.invalidateQueries({ queryKey: ['education-activity-achievements'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  return (
    <EducationPageChrome
      title={t('activityAchievements')}
      description={t('activityAchievementsIntro')}
      extra={
        <Space>
          {canManage ? (
            <Button type="primary" onClick={() => navigate('/education/activities/achievements/new')}>
              {t('newActivityAchievement')}
            </Button>
          ) : null}
          <Button onClick={() => navigate('/education/activities')}>{t('backToActivities')}</Button>
        </Space>
      }
    >
      <Card size="small">
        <Space wrap style={{ marginBottom: 16 }}>
          <Input.Search
            allowClear
            defaultValue={q}
            placeholder={t('searchActivityAchievements')}
            onSearch={(value) => setFilter('q', value.trim())}
            style={{ width: 300 }}
          />
          <Select
            allowClear
            value={level}
            placeholder={t('achievementLevel')}
            onChange={(value) => setFilter('level', value)}
            options={levels.map((value) => ({ value, label: t(`achievementLevel_${value}`) }))}
            style={{ width: 170 }}
          />
        </Space>
        <Table
          rowKey="id"
          size="small"
          loading={achievements.isLoading}
          dataSource={achievements.data || []}
          pagination={{ pageSize: 25 }}
          columns={[
            { title: t('achievementTitle'), dataIndex: 'title' },
            {
              title: t('student'),
              render: (_: unknown, row: AchievementRow) =>
                `${row.admission_no || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`,
            },
            {
              title: t('activity'),
              render: (_: unknown, row: AchievementRow) =>
                `${row.activity_code || ''} — ${row.activity_name || ''}`,
            },
            { title: t('achievedOn'), dataIndex: 'achieved_on', width: 120 },
            {
              title: t('achievementLevel'),
              dataIndex: 'level',
              width: 120,
              render: (value: string) => <Tag>{t(`achievementLevel_${value}`)}</Tag>,
            },
            {
              title: t('evidenceUrl'),
              dataIndex: 'evidence_url',
              width: 100,
              render: (value?: string) =>
                value ? (
                  <a href={value} target="_blank" rel="noreferrer">
                    {t('open')}
                  </a>
                ) : (
                  '—'
                ),
            },
            {
              title: t('actions'),
              width: 170,
              render: (_: unknown, row: AchievementRow) => (
                <Space>
                  <Button size="small" onClick={() => navigate(`/education/activities/achievements/${row.id}`)}>
                    {canManage ? t('edit') : t('open')}
                  </Button>
                  {canManage ? (
                    <Popconfirm
                      title={t('deleteActivityAchievementConfirm')}
                      onConfirm={() => remove.mutate(row.id)}
                    >
                      <Button size="small" danger loading={remove.isPending}>
                        {t('delete')}
                      </Button>
                    </Popconfirm>
                  ) : null}
                </Space>
              ),
            },
          ]}
        />
      </Card>
    </EducationPageChrome>
  );
}
